import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetTask } from 'selectors/tasks';
import { makeGetLoadingState } from 'selectors/global';

import { fetchTask } from 'actions/tasks';
import { fetchMilestone } from 'actions/milestones';

import { useCurrentUser } from 'shared-hooks/user';
import { useCurrentTeam } from 'shared-hooks/team';
import { useProject } from 'shared-hooks/project';
import { useMilestone } from 'shared-hooks/milestone';

import { isTempId } from 'utils/helpers';
import { getMilestoneType } from 'utils/milestone';

export const useTasksScreenTitle = (tasksScreenType, projectId) => {
    const { t } = useTranslation('task');
    const user = useCurrentUser();
    const team = useCurrentTeam();
    const project = useProject(projectId);

    switch (tasksScreenType) {
        case 'user':
            return t('Tasks Name', { name: user?.name || '' });

        case 'project':
        case 'projectCalendar':
            return t('Tasks Name', { name: project?.name || '' });

        case 'team':
            return t('All Tasks Name', { name: team?.name || '' });

        default:
            return t('Tasks');
    }
}

const getTasksLoading = makeGetLoadingState();

export const useTaskGroupTasksLoading = taskGroupId => {
    return useSelector(({ tasks }) => getTasksLoading(tasks.fetchInfo, 'taskGroup', taskGroupId));
}

const getTask = makeGetTask();

export const useTask = (taskId, dontFetch) => {
    const task = useSelector(({ tasks }) => taskId ? getTask(tasks.items, taskId) : null);

    if (taskId && !task && !isTempId(taskId) && !dontFetch) {
        fetchTask(taskId);
    }

    return task;
};

const useIsTaskDepenendant = (taskId, dontFetch) => {
    const task = useTask(taskId, dontFetch);
    const taskDep = useTask(task?.dependency, dontFetch);

    if (!task?.dependency) {
        return [false, null];
    }

    if (!taskDep) {
        return [false, null];
    }

    if (taskDep.completed) {
        return [false, null];
    }

    return [
        true,
        {
            type: 'task',
            id: task.dependency
        }
    ];
}

const useIsMilestoneDepenendant = (taskId, dontFetch) => {
    const task = useTask(taskId, dontFetch);
    const taskMilestone = useMilestone(task?.milestone, dontFetch);
    const taskMilestoneDep = useMilestone(taskMilestone?.dependency, dontFetch);

    if (!task?.milestone) {
        return [false, null];
    }

    if (!taskMilestone) {
        fetchMilestone(task.milestone);
        return [false, null];
    }

    if (!taskMilestone.dependency) {
        return [false, null];
    }

    if (!taskMilestoneDep) {
        fetchMilestone(taskMilestone.dependency);
        return [false, null];
    }

    if (getMilestoneType(taskMilestoneDep) === 'completed') {
        return [false, null];
    }

    return [
        true,
        {
            type: 'milestone',
            id: taskMilestone.dependency
        }
    ];
}

export const useIsDependant = (taskId, dontFetch) => {
    const isTaskDepenendant = useIsTaskDepenendant(taskId, dontFetch);
    const isMilestoneDepenendant = useIsMilestoneDepenendant(taskId, dontFetch);

    if (isMilestoneDepenendant[0]) {
        return isMilestoneDepenendant;
    }

    return isTaskDepenendant;
}

